import { BugOutlined, HomeOutlined, ReloadOutlined } from "@ant-design/icons"
import * as Sentry from "@sentry/react"
import { Button, Collapse, Flex, Result, Typography } from "antd"
import { useEffect, useState } from "react"
import {
  isRouteErrorResponse,
  useNavigate,
  useRouteError
} from "react-router-dom"

const { Text, Paragraph } = Typography

interface ErrorBoundaryFallbackProps {
  error?: Error
}

// A 404 is the router answering a bad URL, not the app breaking; reporting it
// would bury the real crashes under every mistyped link.
const isCrash = (error: unknown): boolean =>
  !isRouteErrorResponse(error) || error.status >= 500

export function ErrorBoundaryFallback({
  error: propError
}: ErrorBoundaryFallbackProps) {
  const routeError = useRouteError()
  const navigate = useNavigate()

  const error = propError || routeError

  const getErrorInfo = () => {
    if (isRouteErrorResponse(error)) {
      return {
        status: error.status,
        title: error.status === 404 ? "Page Not Found" : "Server Error",
        subtitle: error.statusText || "Something went wrong",
        message: error.data?.message || null
      }
    }

    if (error instanceof Error) {
      return {
        status: 500,
        title: "Something Went Wrong",
        subtitle: "An unexpected error occurred",
        message: error.message,
        stack: error.stack
      }
    }

    return {
      status: 500,
      title: "Unknown Error",
      subtitle: "An unexpected error occurred",
      message: String(error)
    }
  }

  const errorInfo = getErrorInfo()

  // The router catches this error, so it never reaches the global handler
  // Sentry installs — without this, a crash here leaves no trace anywhere: the
  // message and stack below are rendered in DEV only, and prod users could
  // report nothing but the bug icon. The id is what turns a support message
  // into the event.
  const [eventId, setEventId] = useState<string | null>(null)

  useEffect(() => {
    if (!error || !isCrash(error)) return
    setEventId(Sentry.captureException(error))
  }, [error])

  const handleReload = () => {
    window.location.reload()
  }

  const handleGoHome = () => {
    navigate("/")
  }

  const handleGoBack = () => {
    navigate(-1)
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4 sm:p-6 md:p-8">
      <div className="w-full max-w-lg">
        <Result
          status="error"
          icon={<BugOutlined className="text-5xl sm:text-6xl text-red-400" />}
          title={
            <Text className="text-xl sm:text-2xl font-semibold text-gray-800">
              {errorInfo.title}
            </Text>
          }
          subTitle={
            <Flex vertical gap={4} align={"center"}>
              <Text className="text-sm sm:text-base text-gray-500">
                {errorInfo.subtitle}
              </Text>
              {eventId && (
                <Text
                  copyable={{ text: eventId }}
                  className="text-xs text-gray-400"
                  data-testid="error-reference"
                >
                  {`Reference: ${eventId}`}
                </Text>
              )}
            </Flex>
          }
          extra={
            <Flex vertical gap={16} className="w-full">
              {/* Action buttons */}
              <Flex gap={12} wrap="wrap" justify="center" className="w-full">
                <Button
                  type="primary"
                  icon={<ReloadOutlined />}
                  onClick={handleReload}
                  size="large"
                  className="min-w-32"
                >
                  Reload
                </Button>

                <Button
                  icon={<HomeOutlined />}
                  onClick={handleGoHome}
                  size="large"
                  className="min-w-32"
                >
                  Go Home
                </Button>

                <Button
                  onClick={handleGoBack}
                  size="large"
                  className="min-w-32 sm:hidden"
                >
                  Go Back
                </Button>
              </Flex>

              {/* Error details (dev mode) */}
              {import.meta.env.DEV && errorInfo.message && (
                <Collapse
                  ghost
                  className="bg-white rounded-lg border border-gray-200"
                  items={[
                    {
                      key: "details",
                      label: (
                        <Text className="text-gray-600 text-sm">
                          Error Details (dev)
                        </Text>
                      ),
                      children: (
                        <div className="space-y-3">
                          <div>
                            <Text
                              strong
                              className="text-xs text-gray-500 uppercase"
                            >
                              Message:
                            </Text>
                            <Paragraph
                              code
                              copyable
                              className="mt-1 text-xs sm:text-sm break-all"
                            >
                              {errorInfo.message}
                            </Paragraph>
                          </div>

                          {errorInfo.stack && (
                            <div>
                              <Text
                                strong
                                className="text-xs text-gray-500 uppercase"
                              >
                                Stack trace:
                              </Text>
                              <pre className="mt-1 p-3 bg-gray-900 text-green-400 text-xs rounded-lg overflow-x-auto max-h-48 scrollbar-thin scrollbar-thumb-gray-600">
                                {errorInfo.stack}
                              </pre>
                            </div>
                          )}
                        </div>
                      )
                    }
                  ]}
                />
              )}
            </Flex>
          }
        />
      </div>
    </div>
  )
}
